// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the FYI card on the phone inbox (the smarter Tidy up). The server marks
// mail worth seeing once that needs no reply (konvo_api KonvoMailFyi); this
// groups it for the card by kind (GitHub, deploy runs, digests, meeting
// replies) and lists the automated mail that stayed, and why. A dismissed card
// stays quiet until newer FYI mail arrives.

import type {KonvoMailFyiKind, KonvoMailMessage} from '@app/features/konvo_mail/state/KonvoMail';

/** `archive_read`: archived once read. `skip`: archived as it arrives. `never`: not FYI. */
export type MailFyiRule = 'archive_read' | 'skip' | 'never';

const RULES = new Set<string>(['archive_read', 'skip', 'never']);
const GROUP_KEY = /^(runs|digests|meeting_replies|n:[a-z0-9.-]{1,120})$/;

/** Mirrors konvo_api sanitizeMailFyiRules. */
export function sanitizeMailFyiRules(raw: unknown): Record<string, MailFyiRule> {
	const out: Record<string, MailFyiRule> = {};
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
	for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
		const group = key.trim().toLowerCase();
		if (GROUP_KEY.test(group) && typeof value === 'string' && RULES.has(value)) out[group] = value as MailFyiRule;
	}
	return out;
}

export interface MailFyiGroup {
	key: string;
	kind: KonvoMailFyiKind;
	/** The service of the newest mail ("GitHub"); the card names runs, digests and meeting replies itself. */
	source: string;
	/** Newest first. */
	messages: Array<KonvoMailMessage>;
	/** Distinct, newest first. */
	topics: Array<string>;
	actors: Array<string>;
	sources: Array<string>;
}

export interface MailFyiCardState {
	groups: Array<MailFyiGroup>;
	total: number;
	/** Automated mail that stayed in the inbox (asks you, failed run), newest first. */
	kept: Array<KonvoMailMessage>;
	/** The newest FYI mail's time: dismissing the card keeps it hidden until something newer. */
	newestAt: string;
}

function time(message: KonvoMailMessage): number {
	return message.received_at ? Date.parse(message.received_at) || 0 : 0;
}

function pushDistinct(list: Array<string>, value: string | null | undefined): void {
	const trimmed = value?.trim();
	if (trimmed && !list.includes(trimmed)) list.push(trimmed);
}

/** "Markus Mulder" -> "Markus"; names that are not a person's are kept whole. */
export function firstName(name: string): string {
	const first = name.trim().split(/\s+/)[0] ?? name;
	return /^[A-Z][a-z'-]+$/.test(first) ? first : name.trim();
}

/** FYI mail by group, biggest first (then newest). Pinned and draft mail never counts. */
export function fyiGroups(messages: ReadonlyArray<KonvoMailMessage>): Array<MailFyiGroup> {
	const byKey = new Map<string, MailFyiGroup>();
	const sorted = [...messages].sort((a, b) => time(b) - time(a));
	for (const message of sorted) {
		const fyi = message.fyi;
		if (!fyi || message.is_draft || message.is_pinned) continue;
		let group = byKey.get(fyi.group);
		if (!group) {
			group = {key: fyi.group, kind: fyi.kind, source: fyi.source, messages: [], topics: [], actors: [], sources: []};
			byKey.set(fyi.group, group);
		}
		group.messages.push(message);
		pushDistinct(group.topics, fyi.topic);
		pushDistinct(group.actors, fyi.actor ? firstName(fyi.actor) : null);
		pushDistinct(group.sources, fyi.source);
	}
	return [...byKey.values()].sort(
		(a, b) => b.messages.length - a.messages.length || time(b.messages[0]!) - time(a.messages[0]!),
	);
}

/** What the card shows, or null when there is too little FYI mail or it was dismissed since. */
export function fyiCardState(
	messages: ReadonlyArray<KonvoMailMessage>,
	threshold: number,
	dismissedAt: string | null,
): MailFyiCardState | null {
	const groups = fyiGroups(messages);
	const total = groups.reduce((sum, group) => sum + group.messages.length, 0);
	if (total === 0 || total < threshold) return null;
	const newest = groups.map((group) => group.messages[0]!).sort((a, b) => time(b) - time(a))[0]!;
	if (dismissedAt && time(newest) <= (Date.parse(dismissedAt) || 0)) return null;
	const kept = messages.filter((message) => message.attention && !message.is_draft).sort((a, b) => time(b) - time(a));
	return {groups, total, kept, newestAt: newest.received_at ?? new Date(0).toISOString()};
}

const KNOWN_SOURCES: Record<string, string> = {
	'github.com': 'GitHub',
	'gitlab.com': 'GitLab',
	'clickup.com': 'ClickUp',
	'atlassian.net': 'Jira',
	'linear.app': 'Linear',
	'slack.com': 'Slack',
	'figma.com': 'Figma',
	'notion.so': 'Notion',
	'sentry.io': 'Sentry',
};

/** What a rule's group key stands for, for Settings: `n:github.com` -> GitHub notifications. */
export function fyiRuleGroup(key: string): Pick<MailFyiGroup, 'kind' | 'source'> {
	if (key === 'runs') return {kind: 'run', source: ''};
	if (key === 'digests') return {kind: 'digest', source: ''};
	if (key === 'meeting_replies') return {kind: 'meeting_reply', source: ''};
	const domain = key.replace(/^n:/, '');
	return {kind: 'notification', source: KNOWN_SOURCES[domain] ?? domain};
}
